import React, { useEffect, useState } from 'react';
import { Popover, TextField, OptionList, Spinner, Text, Button } from '@shopify/polaris';
import { EditMinor } from '@shopify/polaris-icons';
import { api } from '../utils/api';

// An edit icon that opens a product search, for switching which product a row in
// a list applies to. Picking a result calls onChange with the Shopify product
// ({ id, title, variants, hasMoreVariants }). The results are a plain list inside
// the popover rather than an Autocomplete, whose own dropdown can't open inside one.
function ProductPicker({ onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return undefined;
    }
    let current = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await api.get(`/products/search/shopify?query=${encodeURIComponent(query.trim())}`);
        if (current) setResults(response.data);
      } catch (err) {
        console.error('Product search error:', err);
        if (current) setResults([]);
      } finally {
        if (current) {
          setLoading(false);
          setSearched(true);
        }
      }
    }, 300);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [query, open]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const activator = (
    <Button plain icon={EditMinor} onClick={() => setOpen(true)} accessibilityLabel="Change product" />
  );

  return (
    <Popover active={open} activator={activator} onClose={close} preferredAlignment="left">
      <div style={{ width: '320px', padding: '12px' }}>
        <TextField
          label="Product"
          labelHidden
          value={query}
          onChange={setQuery}
          placeholder="Start typing to search products..."
          autoComplete="off"
          autoFocus
        />
      </div>
      {loading && (
        <div style={{ padding: '0 12px 12px', textAlign: 'center' }}>
          <Spinner size="small" />
        </div>
      )}
      {!loading && results.length > 0 && (
        <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
          <OptionList
            options={results.map((product) => ({ value: product.id, label: product.title }))}
            selected={[]}
            onChange={([productId]) => {
              const product = results.find((result) => result.id === productId);
              close();
              if (product) onChange(product);
            }}
          />
        </div>
      )}
      {!loading && searched && results.length === 0 && (
        <div style={{ padding: '0 12px 12px' }}>
          <Text as="p" color="subdued">No products found</Text>
        </div>
      )}
    </Popover>
  );
}

export default ProductPicker;
